import { Calendar, Check, X, Trash2, Clock } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useEvents, useUpdateEvent, useDeleteEvent } from "@/hooks/useAdminData";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export default function Events() {
  const { data: events, isLoading } = useEvents();
  const updateEvent = useUpdateEvent();
  const deleteEvent = useDeleteEvent();

  const handleToggle = async (id: string, isActive: boolean) => {
    try {
      await updateEvent.mutateAsync({ id, is_active: isActive });
      toast.success(isActive ? "Событие активировано" : "Событие деактивировано");
    } catch (error) {
      toast.error("Ошибка при обновлении");
    }
  };

  const handleDelete = async (id: string, name: string) => {
    try {
      await deleteEvent.mutateAsync(id);
      toast.success(`Событие "${name}" удалено`);
    } catch (error) {
      toast.error("Ошибка при удалении. Возможно, событие используется в генерациях.");
    }
  };

  const isExpired = (expiresAt: string | null) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const formatExpiry = (expiresAt: string) => {
    return new Date(expiresAt).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <AdminLayout title="События" description="Управление событиями для генерации">
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" />
            Список событий
          </CardTitle>
          <CardDescription>Включите или выключите события</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {events?.map((event) => (
                <div key={event.id} className="flex items-center justify-between p-4 rounded-lg bg-muted/50 border border-border/50">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-xl">
                      {event.emoji}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{event.name}</p>
                        {event.expires_at && (
                          <Badge variant={isExpired(event.expires_at) ? "destructive" : "secondary"} className="text-[10px] px-1.5 py-0">
                            {isExpired(event.expires_at) ? "⏳ Истекло" : `🔥 до ${formatExpiry(event.expires_at)}`}
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">Код: {event.code}</p>
                      {event.seasonal_prompt && (
                        <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-xs" title={event.seasonal_prompt}>
                          <Clock className="w-3 h-3 inline mr-1" />Спецпромпт
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {event.is_active ? (
                      <span className="text-xs text-primary flex items-center gap-1">
                        <Check className="w-3 h-3" /> Активно
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <X className="w-3 h-3" /> Неактивно
                      </span>
                    )}
                    <Switch
                      checked={event.is_active}
                      onCheckedChange={(checked) => handleToggle(event.id, checked)}
                    />
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive hover:bg-destructive/10">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Удалить событие «{event.name}»?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Это действие нельзя отменить. Если событие используется в генерациях, удаление может не сработать.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Отмена</AlertDialogCancel>
                          <AlertDialogAction
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            onClick={() => handleDelete(event.id, event.name)}
                          >
                            Удалить
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
